/**
 * Konflikt-Auflösungs-Seite für Timesheet oder Expense.
 *
 * Aufruf: /conflicts/timesheet/:id  oder  /conflicts/expense/:id
 *
 * Glass-Box-Prinzip: Single-Source. Diese Page nutzt den GLEICHEN ConflictResolver
 * wie Werkstatt-Phase 2 und Phase 7. Vorher war hier duplizierte Action-Logik mit
 * leeren PDF-iframes — das wurde komplett ersetzt durch ConflictResolver-Embed +
 * PDF-Vorschau-Links zur Validierung.
 *
 * Für Expenses bleibt die spezielle Diff-Tabelle (entry_conflict, merge_proposal)
 * — die hat ConflictResolver nicht, weil Expense-Konflikte per-entry sind.
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation, Link, type Location } from "react-router-dom";
import { api, ApiError, type Card, type Employee, type Timesheet } from "../lib/api";
import { ConflictResolver } from "../components/werkstatt/ConflictResolver";
import { DetailModal } from "../components/DetailModal";

type Kind = "timesheet" | "expense";

interface GenericCard {
  id: string;
  employee_id: string | null;
  month: string;
  state: string;
  pdf_orig_filename: string;
  data: any;
  first_name?: string | null;
  last_name?: string | null;
}

async function fetchCard(kind: Kind, id: string): Promise<GenericCard> {
  const r = await fetch(new URL(`api/${kind}s/${id}`, document.baseURI).toString(), {
    credentials: "same-origin",
  });
  if (!r.ok) throw new ApiError(r.status, await r.text(), null);
  const body = await r.json();
  return body[kind];
}

async function postAction(kind: Kind, id: string, action: string, extras: Record<string, unknown> = {}): Promise<void> {
  const r = await fetch(new URL(`api/${kind}s/${id}/resolve-conflict`, document.baseURI).toString(), {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ action, ...extras }),
  });
  if (!r.ok) throw new Error(await r.text());
}

export function ConflictPage() {
  const { kind, id } = useParams<{ kind: Kind; id: string }>();
  const nav = useNavigate();
  const loc = useLocation();
  const background = (loc.state as { background?: Location } | null)?.background;
  const isModal = !!background;
  const [newCard, setNewCard] = useState<GenericCard | null>(null);
  const [existing, setExisting] = useState<GenericCard | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!kind || !id) return;
    setErr(null);
    fetchCard(kind, id)
      .then((card) => {
        setNewCard(card);
        const existId =
          card.data?.conflict?.existing_card_id ?? card.data?.merge_proposal?.target_card_id;
        if (!existId) {
          setExisting(null);
          return;
        }
        return fetchCard(kind, existId).then(setExisting);
      })
      .catch((e) => setErr(e.message));
    // Load employees for reassign-dropdown
    api.employees.list((newCard as any)?.client_id).then(setEmployees).catch(() => {});
  }, [kind, id, reloadKey]);

  if (err && !newCard) {
    const inner = <div style={{ padding: 24, color: "#dc2626" }}>Fehler: {err}</div>;
    return isModal ? <DetailModal dirty={false}>{inner}</DetailModal> : inner;
  }
  if (!newCard) {
    const inner = <div style={{ padding: 24 }}>Lade…</div>;
    return isModal ? <DetailModal dirty={false}>{inner}</DetailModal> : inner;
  }

  const nameLabel = (c: GenericCard) =>
    c.first_name && c.last_name ? `${c.last_name}, ${c.first_name}` : "—";

  // Expense conflict — echter Merge-View: beide Scans nebeneinander + klare
  // "lebt/stirbt"-Aktionen, damit die Entscheidung visuell treffbar ist.
  if (kind === "expense") {
    const empName = (c: GenericCard | null): string => {
      if (!c) return "—";
      if (c.last_name && c.first_name) return `${c.last_name}, ${c.first_name}`;
      const e = employees.find((x) => x.id === c.employee_id);
      return e ? `${e.last_name}, ${e.first_name}` : "—";
    };
    const conflicts = newCard.data?.merge_proposal?.diff?.conflicts ?? [];
    const isEntryConflict =
      newCard.data?.conflict?.reason === "entry_conflict" || conflicts.length > 0;
    const inner = (
      <div className="conflict-page">
        <div className="ts-detail-header">
          <button className="link" onClick={() => nav(-1)}>← Zurück</button>
          <h2>Konflikt · Diäten · {newCard.month}</h2>
          <span className="muted">Mitarbeiter: {empName(newCard)}</span>
        </div>
        {err && <div style={{ color: "#dc2626", marginTop: 8 }}>{err}</div>}
        <ExpenseConflictView
          newCard={newCard}
          existing={existing}
          empName={empName}
          isEntryConflict={isEntryConflict}
          conflicts={conflicts}
          onMerge={async () => { try { await postAction(kind, id!, "merge_confirmed"); nav(-1); } catch (e: any) { setErr(e.message); } }}
          onDiscard={async () => { try { await postAction(kind, id!, "discard"); nav(-1); } catch (e: any) { setErr(e.message); } }}
          onOverwrite={async () => { try { await postAction(kind, id!, "overwrite"); nav(-1); } catch (e: any) { setErr(e.message); } }}
        />
      </div>
    );
    return isModal ? <DetailModal dirty={false}>{inner}</DetailModal> : inner;
  }

  // Timesheet path — use the canonical ConflictResolver (same as Werkstatt-Phase 2 / Phase 7)
  const inner = (
    <div className="conflict-page">
      <div className="ts-detail-header">
        <button className="link" onClick={() => nav(-1)}>← Zurück</button>
        <h2>Konflikt · Stundenliste · {newCard.month}</h2>
        <span className="muted">für Mitarbeiter: {nameLabel(newCard)}</span>
      </div>

      {/* PDF-Vorschau-Links (statt der vorher leeren iframes) */}
      <div style={{
        display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12,
        margin: "12px 0", padding: 12, background: "#f9fafb",
        border: "1px solid #e5e7eb", borderRadius: 6,
      }}>
        <div>
          <strong style={{ fontSize: 13 }}>Bestehend ({existing?.state ?? "?"})</strong>
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
            {existing ? (
              <>
                <code>{existing.id.slice(0, 8)}</code> · {existing.pdf_orig_filename}<br />
                <a href={api.timesheets.pdfUrl(existing.id)} target="_blank" rel="noreferrer">PDF öffnen ↗</a>
                {" · "}
                <Link to={`/timesheets/${existing.id}`}>Detail-View →</Link>
              </>
            ) : "—"}
          </div>
        </div>
        <div>
          <strong style={{ fontSize: 13 }}>Neu (wartet auf Entscheidung)</strong>
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
            <code>{newCard.id.slice(0, 8)}</code> · {newCard.pdf_orig_filename}<br />
            <a href={api.timesheets.pdfUrl(newCard.id)} target="_blank" rel="noreferrer">PDF öffnen ↗</a>
            {" · "}
            <Link to={`/timesheets/${newCard.id}`}>Detail-View →</Link>
          </div>
        </div>
      </div>

      {/* Canonical resolver — gleicher Code wie Werkstatt */}
      <ConflictResolver
        card={newCard as unknown as Card}
        timesheet={newCard as unknown as Timesheet}
        employees={employees}
        onResolved={() => {
          // Reload card; if resolved out of pending_conflict, go back
          setReloadKey(k => k + 1);
          setTimeout(() => {
            fetchCard(kind!, id!).then(c => {
              if (c.state !== "pending_conflict") nav(-1);
            }).catch(() => {});
          }, 400);
        }}
      />

      {err && <div style={{ color: "#dc2626", marginTop: 8 }}>{err}</div>}
    </div>
  );
  return isModal ? <DetailModal dirty={false}>{inner}</DetailModal> : inner;
}

function cellV(c: any): string {
  if (!c) return "";
  if (typeof c === "string") return c;
  return c.value ?? "";
}
function expensePdfUrl(c: GenericCard): string {
  return new URL(`api/expenses/${c.id}/pdf`, document.baseURI).toString();
}
function expensePreviewUrl(c: GenericCard): string {
  const rot = c.data?.meta?.spatial?.rotation_applied_cw ?? 0;
  return new URL(`api/cards/expense/${c.id}/preview?rotate=${rot}&dpi=130`, document.baseURI).toString();
}

/** Eine Karten-Spalte: Scan-Vorschau + Mitarbeiter + Quelle + Einträge. */
function ExpenseCardCol({
  title, accent, card, empName,
}: {
  title: string; accent: string; card: GenericCard | null; empName: (c: GenericCard | null) => string;
}) {
  if (!card) {
    return <div style={{ border: `2px dashed ${accent}`, borderRadius: 8, padding: 16, color: "#6b7280" }}>{title}: keine</div>;
  }
  const entries = (card.data?.entries ?? []).filter(
    (e: any) => cellV(e?.datum) || cellV(e?.abfahrt) || cellV(e?.rueckkehr),
  );
  return (
    <div style={{ border: `2px solid ${accent}`, borderRadius: 8, overflow: "hidden", background: "#fff" }}>
      <div style={{ background: accent, color: "#fff", padding: "6px 10px", fontWeight: 700, fontSize: 13 }}>{title}</div>
      <div style={{ padding: 10 }}>
        <div style={{ fontSize: 12, color: "#374151", marginBottom: 6, lineHeight: 1.5 }}>
          <strong>{empName(card)}</strong> · {entries.length} Einträge · <span style={{ color: "#6b7280" }}>{card.state}</span><br />
          <span style={{ color: "#6b7280" }}>{card.pdf_orig_filename} · <code>{card.id.slice(0, 8)}</code></span>{" "}
          <a href={expensePdfUrl(card)} target="_blank" rel="noreferrer">PDF ↗</a> ·{" "}
          <Link to={`/expenses/${card.id}`}>Detail →</Link>
        </div>
        <a href={expensePdfUrl(card)} target="_blank" rel="noreferrer" title="Original-PDF öffnen">
          <img src={expensePreviewUrl(card)} alt="Scan-Vorschau" loading="eager"
               style={{ width: "100%", border: "1px solid #e5e7eb", borderRadius: 4, display: "block" }} />
        </a>
        <table className="data-table" style={{ width: "100%", marginTop: 8, fontSize: 11 }}>
          <thead><tr><th style={{ textAlign: "left" }}>Datum</th><th>Abfahrt</th><th>Rückkehr</th></tr></thead>
          <tbody>
            {entries.length === 0 && <tr><td colSpan={3} style={{ color: "#9ca3af" }}>keine Einträge gelesen</td></tr>}
            {entries.slice(0, 40).map((e: any, i: number) => (
              <tr key={i}>
                <td style={{ textAlign: "left" }}>{cellV(e.datum) || "—"}</td>
                <td>{cellV(e.abfahrt) || "—"}</td>
                <td>{cellV(e.rueckkehr) || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Eine Aktion mit klarer "lebt/stirbt"-Erklärung. */
function ActionRow({
  color, label, desc, onClick, disabled, disabledHint,
}: {
  color: string; label: string; desc: string; onClick: () => void; disabled?: boolean; disabledHint?: string;
}) {
  return (
    <div style={{ display: "flex", gap: 12, alignItems: "center", opacity: disabled ? 0.45 : 1 }}>
      <button onClick={onClick} disabled={disabled}
              style={{ background: color, color: "#fff", border: 0, borderRadius: 6, padding: "9px 14px",
                       fontWeight: 700, fontSize: 13, cursor: disabled ? "not-allowed" : "pointer",
                       minWidth: 230, textAlign: "left", flexShrink: 0 }}>{label}</button>
      <span style={{ fontSize: 12.5, color: "#374151", lineHeight: 1.45 }}>
        {desc}{disabled && disabledHint ? ` — ${disabledHint}` : ""}
      </span>
    </div>
  );
}

/** Werte-Konflikt-Tabelle (nur Anzeige; Aktionen liegen in ExpenseConflictView). */
function ExpenseEntryDiff({ conflicts }: { conflicts: Array<{ existing: any; new: any }> }) {
  const keys = ["abfahrt", "rueckkehr", "kz_nr", "zm_abzug", "m_verr", "kunde"] as const;
  return (
    <section style={{ marginTop: 14 }}>
      <h3 style={{ fontSize: 14 }}>⚠ {conflicts.length} Werte-Konflikt{conflicts.length === 1 ? "" : "e"} — dieselbe Zeile, andere Werte</h3>
      <table className="data-table conflict-diff" style={{ fontSize: 12 }}>
        <thead><tr><th>Datum</th><th>Ort</th><th>Feld</th><th>Bestehend (links)</th><th>Neu (rechts)</th></tr></thead>
        <tbody>
          {conflicts.flatMap((c, i) =>
            keys.filter((k) => cellV(c.existing?.[k]) !== cellV(c.new?.[k])).map((k) => (
              <tr key={`${i}-${k}`}>
                <td>{cellV(c.existing?.datum)}</td>
                <td>{cellV(c.existing?.ort)}</td>
                <td><code>{k}</code></td>
                <td style={{ background: "#eff6ff" }}>{cellV(c.existing?.[k]) || "—"}</td>
                <td style={{ background: "#fff7ed" }}>{cellV(c.new?.[k]) || "—"}</td>
              </tr>
            )),
          )}
        </tbody>
      </table>
    </section>
  );
}

/** Der Merge-View: beide Scans nebeneinander + Ergebnis-Vorschau + 3 Aktionen. */
function ExpenseConflictView({
  newCard, existing, empName, isEntryConflict, conflicts, onMerge, onDiscard, onOverwrite,
}: {
  newCard: GenericCard;
  existing: GenericCard | null;
  empName: (c: GenericCard | null) => string;
  isEntryConflict: boolean;
  conflicts: Array<{ existing: any; new: any }>;
  onMerge: () => void; onDiscard: () => void; onOverwrite: () => void;
}) {
  const merged = newCard.data?.merge_proposal?.diff?.merged_preview ?? [];
  const newEntries = newCard.data?.merge_proposal?.diff?.new_entries ?? [];
  return (
    <section>
      <div style={{ padding: 10, background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 6, fontSize: 13, margin: "10px 0" }}>
        Zwei Diäten-Seiten für <strong>denselben Mitarbeiter & Monat</strong>. Vergleiche beide Scans und entscheide unten, welche Daten gelten.
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, alignItems: "start" }}>
        <ExpenseCardCol title="◀ BESTEHEND (im System)" accent="#2563eb" card={existing} empName={empName} />
        <ExpenseCardCol title="NEU (wartet auf Entscheidung) ▶" accent="#d97706" card={newCard} empName={empName} />
      </div>

      {isEntryConflict && conflicts.length > 0 && <ExpenseEntryDiff conflicts={conflicts} />}

      <div style={{ marginTop: 14, padding: 14, border: "1px solid #e5e7eb", borderRadius: 8, background: "#f9fafb" }}>
        <div style={{ fontWeight: 700, marginBottom: 10, fontSize: 14 }}>Was soll passieren?</div>
        <div style={{ display: "grid", gap: 10 }}>
          <ActionRow
            color="#16a34a" label="Zusammenführen"
            desc={isEntryConflict
              ? "Sauberes Kombinieren nicht möglich, weil dieselben Zeilen unterschiedliche Werte haben (siehe Tabelle oben)."
              : `Beide kombinieren: BESTEHENDE behält alles + ${newEntries.length} neue Einträge dazu (Ergebnis ${merged.length || "?"}). Neue Karte wird archiviert (Audit). → links lebt, rechts wird einverleibt.`}
            onClick={onMerge} disabled={isEntryConflict} disabledHint="bei Werte-Konflikten gesperrt" />
          <ActionRow
            color="#2563eb" label="◀ Nur BESTEHENDE behalten"
            desc="Die neue Seite verwerfen (wird archiviert). Bestehende bleibt unverändert. → links lebt, rechts stirbt."
            onClick={onDiscard} />
          <ActionRow
            color="#d97706" label="Nur NEUE behalten ▶"
            desc="Die neue Seite ersetzt die bestehende (bestehende → superseded, Audit bleibt). → rechts lebt, links stirbt."
            onClick={onOverwrite} />
        </div>
      </div>
    </section>
  );
}
